import { getTheme, INavLinkGroup, INavStyles, Nav } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPSeparator, IPSpinner, IPSpinnerSize, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import { PageHeaderVersion } from 'components/SharePoint/PageRenderer/SharePointPageHeaders';
import {
  extLinkIcon,
  globalStackStylesHeight100,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  globalTextStylesError,
} from 'globalStyles';
import ResourceLink, { ResourceLinkVersion } from 'models/resourceLink';
import { UserReadConfirmation } from 'models/userReadConfirmation';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataUserLinks } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { sortOnString } from 'utils/sorting';
import { overflow } from 'utils/string';
import HrefLink from '../HrefLink';
import { LinkPreview } from './LinkPreview';

export interface ILinkConfirmationModalProps {
  links: ResourceLink[];
  selectedLinkId: number | undefined;
  isOpen: boolean;
  onClose: (updatedLinks: ResourceLink[]) => void;
  onConfirmed?: (usr: UserReadConfirmation) => void;
}

const getFirstLinkToConfirm = (selectedLinkId: number | undefined, links: ResourceLink[]): ResourceLink | undefined => {
  //When the selected link from props has unread RC, return it
  //Otherwise return the first link
  const selLink = links.find((l) => l.linkId === selectedLinkId);
  if (selLink?.hasRCUnread()) {
    return selLink;
  } else if (links.length > 0) {
    return links[0];
  } else {
    return undefined;
  }
};

const navStyles: Partial<INavStyles> = {
  root: {
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  link: {
    whiteSpace: 'normal',
    lineHeight: 'inherit',
  },
};

const LinkConfirmationModal = (props: ILinkConfirmationModalProps) => {
  const { t } = useTranslation(['translation', 'publication']);
  const appContext = useContext(AppContext);
  const [links, setLinks] = useState<ResourceLink[]>(props.links);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [versionInfo, setVersionInfo] = useState<ResourceLinkVersion | undefined>(undefined);
  const [selectedLink, setSelectedLink] = useState<ResourceLink | undefined>(undefined);
  const theme = getTheme();

  //
  // Effects
  //
  useEffect(() => {
    if (props.isOpen) {
      if (props.links.length > 0) {
        setLinks(props.links);
        setSelectedLink(getFirstLinkToConfirm(props.selectedLinkId, props.links));
      } else {
        //When no links are provided, load those needing confirmation
        loadData();
      }
    } else {
      setLinks([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.links]);

  useEffect(() => {
    setVersionInfo(undefined);
  }, [selectedLink]);

  //
  // Loading
  //
  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.lists.getItems();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const links = await apiGetWidgetDataUserLinks(accessToken, appContext.globalDataCache);

      const linksToConfirm = links.filter((l) => l.hasRCUnread());
      links.sort((a, b) => {
        return sortOnString(a.linkName, b.linkName);
      });

      setLinks(linksToConfirm);
      setSelectedLink(getFirstLinkToConfirm(props.selectedLinkId, linksToConfirm));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Helpers
  //
  const onConfirmed = (usr: UserReadConfirmation) => {
    //set selected link as confirmed
    if (!selectedLink) return;
    selectedLink.setRC(usr);
    const newLinks = links.map((l) => (l.linkId === selectedLink.linkId ? selectedLink : l));
    setLinks(newLinks);
    if (props.onConfirmed) props.onConfirmed(usr);

    //close when all links are confirmed
    const remainingLinks = newLinks.filter((l) => l.hasRCUnread());
    if (remainingLinks.length === 0) {
      props.onClose(newLinks);
      appContext.showNotification(t('publication:ReadConfirmation.AllLinksConfirmed'));
    }
  };

  const goToNext = () => {
    if (!links || links.length === 0) return;

    if (!selectedLink) {
      setSelectedLink(links[0]);

      return;
    }

    //select the first unconfirmed link, starting from the selected one
    //when the end of the list is reached, start from the beginning
    //when there are no more unconfirmed links, close the modal
    const idx = links.findIndex((l) => l.linkId === selectedLink?.linkId);

    for (let i = idx + 1; i < links.length; i++) {
      const link = links[i];
      if (link.hasRCUnread()) {
        setSelectedLink(link);

        return;
      }
    }

    for (let i = 0; i < idx; i++) {
      const link = links[i];
      if (link.hasRCUnread()) {
        setSelectedLink(link);

        return;
      }
    }

    //no more unconfirmed links
    props.onClose(links);
  };

  const getNavGroups = (): INavLinkGroup[] => {
    const navLinks: INavLinkGroup[] = [];
    const mainGroup: INavLinkGroup = { links: [] };

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      mainGroup.links.push({
        name: overflow(link.linkName, 60),
        title: link.linkName,
        url: '',
        key: link.linkId.toString(),
        onclick: () => setSelectedLink(link),
      });
    }

    navLinks.push(mainGroup);

    return navLinks;
  };

  //
  // Main render
  //
  return (
    <GenericModal
      title={t('publication:ReadConfirmation.DialogTitle')}
      isOpen={props.isOpen}
      onClose={() => props.onClose(links)}
      minHeight={400}
      height={'93vh'}
      width={'90vw'}
      maxWidth={appContext.isMobileView ? '90vw' : '1600px'}
      maxHeight={'93vh'}
    >
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText>{t('publication:ReadConfirmation.DialogInfo')}</IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <IPStack.Item grow>
          <IPStack horizontal tokens={globalStackTokensGapMedium} styles={globalStackStylesHeight100}>
            {!appContext.isMobileView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: '20%', minWidth: 200 } }}>
                  <Nav
                    selectedKey={selectedLink?.linkId.toString()}
                    groups={getNavGroups()}
                    onLinkClick={(ev, item) => {
                      const linkId = Number.parseInt(item?.key ?? '0');
                      if (linkId) setSelectedLink(links.find((l) => l.linkId === linkId));
                    }}
                    styles={navStyles}
                  />
                </IPStack.Item>
                <IPSeparator vertical />
              </Fragment>
            )}
            <IPStack.Item styles={{ root: { width: appContext.isMobileView ? '100%' : '75%', minWidth: 300 } }}>
              <IPStack verticalFill>
                <IPStack
                  horizontal
                  verticalAlign="center"
                  styles={{
                    root: {
                      height: appContext.isMobileView ? 48 : 32,
                      borderRadius: '3px',
                      background: appContext.useDarkMode ? theme.palette.themeDarker : theme.palette.themeLighter,
                      overflow: 'hidden',
                      padding: 10,
                    },
                  }}
                  tokens={globalStackTokensGapMedium}
                >
                  {appContext.isMobileView && (
                    <IPStack>
                      <IPStack.Item>
                        <IPText block nowrap styles={globalTextStylesBold}>
                          {selectedLink?.linkName}
                        </IPText>
                      </IPStack.Item>
                      <IPStack.Item>
                        <PageHeaderVersion
                          versionInfo={versionInfo}
                          owner={undefined}
                          publishingState={selectedLink?.publishingState}
                        />
                      </IPStack.Item>
                      {!isLoading && versionInfo && !versionInfo.hasData() && (
                        <IPStack.Item>
                          <IPText styles={globalTextStylesError}>{t('sharepoint:Errors.AccessDenied')}</IPText>
                        </IPStack.Item>
                      )}
                    </IPStack>
                  )}
                  {!appContext.isMobileView && (
                    <Fragment>
                      <IPStack.Item>
                        <PageHeaderVersion
                          versionInfo={versionInfo}
                          owner={selectedLink}
                          publishingState={selectedLink?.publishingState}
                        />
                      </IPStack.Item>
                      {versionInfo?.hasData() && !selectedLink?.list?.isVirtual && (
                        <IPStack.Item>
                          <IPStack horizontal tokens={globalStackTokensGapExtraSmall} verticalAlign="center">
                            <IPFontIcon {...extLinkIcon} style={{ fontSize: 18, color: theme.palette.themePrimary }} />
                            <HrefLink href={selectedLink?.linkURL} external={true}>
                              {t('publication:ReadConfirmation.ReadOnSharePoint')}
                            </HrefLink>
                          </IPStack>
                        </IPStack.Item>
                      )}
                      {!isLoading && versionInfo && !versionInfo.hasData() && (
                        <IPStack.Item>
                          <IPText styles={globalTextStylesError}>{t('sharepoint:Errors.AccessDenied')}</IPText>
                        </IPStack.Item>
                      )}
                    </Fragment>
                  )}
                </IPStack>
                {isLoading && selectedLink === undefined && (
                  <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
                    <IPSpinner size={IPSpinnerSize.large} />
                  </IPStack>
                )}
                <IPStack.Item grow>
                  <LinkPreview
                    link={selectedLink}
                    onConfirmed={onConfirmed}
                    onNext={goToNext}
                    hideHelp={true}
                    hideId={true}
                    hideCommandbar={true}
                    hideHeader={true}
                    onVersionLoaded={setVersionInfo}
                    enableLinks={true}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

export default LinkConfirmationModal;
